import { memo, useContext, useState } from 'react'
import * as MUI from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PatientContext } from 'ui/contexts'
import { useSnapshot } from 'domains/sage/hooks'
import { Text } from 'ui/baseComponents'

const STYLES = {
  accordion: {
    '&.MuiAccordion-root.Mui-expanded': {
      margin: 0,
    },
    '@media print': {
      display: 'none',
    },
  },
  list: {
    listStyleType: 'disc',
    pl: 3,
  },
  item: {
    display: 'list-item',
    paddingLeft: 0,
  },
  reasons: {
    listStyleType: 'circle',
    pl: 3,
  },
  reason: {
    display: 'list-item',
    paddingLeft: 0,
    paddingY: 0,
  },
}

const TAKEAWAY_SEPARATOR = ' Why: '

// Takeaways are formatted "<Takeaway> Why: <reason>; <reason>".
const parseTakeaway = (line) => {
  const [takeaway, why] = line.split(TAKEAWAY_SEPARATOR)
  if (!why) {
    return { takeaway: line, reasons: [] }
  }
  const reasons = why
    .split(';')
    .map((reason) => reason.trim())
    .filter(Boolean)
  return { takeaway: takeaway.trim(), reasons }
}

const TopPatientIssues = ({ snapshotMode }) => {
  const { id: patientId } = useContext(PatientContext)
  const { data, loading, errors } = useSnapshot({ patientId, snapshotMode })
  const [expanded, setExpanded] = useState(true)

  if (loading || errors?.length || !data?.length) {
    return null
  }

  const [{ summary, issues, takeaways }] = data
  // Snapshots without takeaways (older, patient or manual) keep the long-form view.
  const hasTakeaways = takeaways?.length > 0

  return (
    <MUI.Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={STYLES.accordion}
      data-cy="top-patient-issues"
    >
      <MUI.AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Text variant="body16B">Top Patient Issues</Text>
      </MUI.AccordionSummary>
      <MUI.AccordionDetails>
        {hasTakeaways ? (
          <MUI.List sx={STYLES.list}>
            {takeaways.map((line, index) => {
              const { takeaway, reasons } = parseTakeaway(line)
              return (
                <MUI.ListItem key={index} sx={STYLES.item}>
                  {reasons.length ? (
                    <>
                      <Text variant="body16B">{takeaway}</Text>
                      <MUI.List sx={STYLES.reasons} disablePadding>
                        {reasons.map((reason, reasonIndex) => (
                          <MUI.ListItem key={reasonIndex} sx={STYLES.reason}>
                            <Text variant="body16">
                              <em>Why:</em> {reason}
                            </Text>
                          </MUI.ListItem>
                        ))}
                      </MUI.List>
                    </>
                  ) : (
                    <Text variant="body16">{line}</Text>
                  )}
                </MUI.ListItem>
              )
            })}
          </MUI.List>
        ) : (
          <>
            {(summary || '').split('\n').map((paragraph, index) =>
              paragraph ? (
                <Text
                  key={index}
                  variant="body16"
                  component="p"
                  style={{ marginBottom: '1rem' }}
                >
                  {paragraph}
                </Text>
              ) : null,
            )}
            {issues?.length > 0 && (
              <MUI.List sx={STYLES.list}>
                {issues.map((issue, index) => (
                  <MUI.ListItem key={index} sx={STYLES.item}>
                    <Text variant="body16">{issue}</Text>
                  </MUI.ListItem>
                ))}
              </MUI.List>
            )}
          </>
        )}
      </MUI.AccordionDetails>
    </MUI.Accordion>
  )
}

export default memo(TopPatientIssues)
